<template>
  <v-dialog
    :value="value"
    :max-width="maxWidth"
    overlay-color="var(--ds-color-reverse)"
    @input="closeDialog"
  >
    <v-card>
      <v-toolbar>
        <v-toolbar-title>
          {{ filterTitleLabel }}
        </v-toolbar-title>
        <v-spacer />
        <v-toolbar-items class="align-items-center">
          <span>
            <StIcon
              class="close-icon"
              icon-name="close"
              @click="closeDialog"
            />
          </span>
        </v-toolbar-items>
      </v-toolbar>

      <v-row
        no-gutters
        class="mt-5 mx-5"
      >
        <GlobalFilterSummary
          v-if="showGlobalFilterFeature"
          :global-filter-by-data-type="globalFilterByDataTypeToShow"
          :data-type="card.dataType"
        />
      </v-row>

      <div style="margin: 1.2rem">
        {{ $t('components.filter.ModalFilters.label.sacListening.listeningSession') }}
      </div>

      <v-row
        no-gutters
        class="mt-5 mx-5"
      >
        <v-col
          v-for="(filter, index) in filtersObj"
          :key="index"
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <component
            :is="filter.component"
            v-model="tempFilter[filter.filterType]"
            v-bind="filter.props"
            :data-type="card.dataType"
            @clearInfo="trackEvent"
          />
        </v-col>
      </v-row>

      <div style="margin-left: 1.2rem">
        {{ $t('components.filter.ModalFilters.label.sacListening.sacSession') }}
      </div>

      <v-row
        no-gutters
        class="mt-5 mx-5"
      >
        <v-col
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <PageFilter
            v-model="tempFilter['pages']"
            :pages-items="pagesFormatted"
            :clearable="displayClearable('pages')"
            @clearInfo="trackEvent"
            @input="changeFilterValue()"
          />
        </v-col>
        <v-col
          v-for="(filter, index) in filtersSacObj"
          :key="index"
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <component
            :is="filter.component"
            v-model="tempFilter[filter.filterType]"
            v-bind="filter.props"
            :channels="channels"
            @clearInfo="trackEvent"
          />
        </v-col>
        <v-col
          cols="12"
          md="4"
          class="pr-4"
          @scroll.stop
        >
          <UserFilter
            v-model="tempFilter['uids']"
            v-bind="getUIDProps"
            :clearable="displayClearable('uids')"
          />
        </v-col>
      </v-row>

      <v-row
        no-gutters
        class="pb-5 px-5"
      >
        <v-spacer />
        <v-spacer />
        <v-btn
          v-if="showClearChanges"
          outlined
          rounded
          class="mr-3"
          @click="clearChangesFilter"
        >
          {{ clearChangeLabel }}
        </v-btn>
        <v-btn
          v-if="showClearFilter"
          outlined
          rounded
          class="mr-3"
          @click="clearFilter"
        >
          {{ clearFilterLabel }}
        </v-btn>
        <v-btn
          outlined
          rounded
          @click="applyFilter"
        >
          {{ applyLabel }}
        </v-btn>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
import StIcon from '@stilingue/st-icon';
import ChannelFilter from './ChannelFilter';
import NegativationFilter from './NegativationFilter';
import CheckedPosts from './CheckedPosts';
import UserFilter from './UserFilter';
import DarkPostFilter from './DarkPostFilter';
import PageFilter from '@/components/filter/PageFilter';
import SacTypeFilter from '@/components/filter/SacTypeFilter';
import sacFilterModel from '@/mixins/sac-filter-model';
import filtersModel from '@/mixins/filters-model';

import { Flags } from '@/plugins/rollout';
import GlobalFilterSummary from '@/components/GlobalFilterSummary';
import { trackEventWithUserProperties } from '@/utils/mixpanel';

import { isEqual, cloneDeep, differenceWith } from 'lodash';
import { mapGetters } from 'vuex';
import {
  DATA_TYPE,
  FILTER_NAMES,
} from '@/store/modules/cards/constants';

export default {
  name: 'FilterSacListeningModal',
  components: {
    StIcon,
    ChannelFilter,
    NegativationFilter,
    CheckedPosts,
    UserFilter,
    DarkPostFilter,
    GlobalFilterSummary,
    PageFilter,
    SacTypeFilter,
  },
  mixins: [
    filtersModel,
    sacFilterModel,
  ],
  props: {
    value: {
      type: Boolean,
      default: true,
    },
    card: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      changeOnPageFilter: false,
      channels: [],
      appliedFilter: {},
      tempFilter: {},
      globalFilterByDataType: {},
      globalFilterByDataTypeToShow: {},
    };
  },
  computed: {
    filterLabel() {
      return this.$t('components.filter.ModalFilters.label.sacListening.title');
    },
    applyLabel() {
      return this.$t('components.filter.ModalFilters.label.button.applyLabel');
    },
    clearFilterLabel() {
      return this.$t('components.filter.ModalFilters.label.button.clearFilter');
    },
    clearChangeLabel() {
      return this.$t('components.filter.ModalFilters.label.button.clearchange');
    },
    ...mapGetters('dashboard', [
      'activeDashboardFilters',
    ]),
    ...mapGetters('featureFlag', [
      'isEnabled',
    ]),
    ...mapGetters('user', [
      'isEditor',
    ]),
    ...mapGetters('filter', [
      'filterByDataType',
    ]),
    showGlobalFilterFeature() {
      return this.isEnabled(Flags.showGlobalFilter) && this.isEditor;
    },
    filtersSacObj() {
      return [
        {
          component: 'SacTypeFilter',
          filterType: FILTER_NAMES.SAC_TYPE,
          props: {
            clearable: this.displayClearable(FILTER_NAMES.SAC_TYPE),
          },
        },
      ];
    },
    filtersObj() {
      const themeItems = [
        {
          text: this.$t('components.filter.ModalFilters.label.sacListening.notClassified'),
          value: 'Não Classificados',
          shouldIgnore: false,
          hasIgnoreOption: true,
        },
        ...(this.activeDashboardFilters?.themes?.map(value => this.generateObjectToNegativationFilter(value)) || []),
      ];

      const tagItems = [
        {
          text: this.$t('components.filter.ModalFilters.label.sacListening.notClassified'),
          value: 'Sem Tags',
          shouldIgnore: false,
          hasIgnoreOption: true,
        },
        ...(this.activeDashboardFilters?.tags?.map(value => this.generateObjectToNegativationFilter(value)) || []),
      ];

      const namesItems = [
        {
          text: this.$t('components.filter.ModalFilters.label.sacListening.notClassified'),
          value: 'Sem Grupos',
          shouldIgnore: false,
          hasIgnoreOption: true,
        },
        ...(this.activeDashboardFilters?.names?.map(value => this.generateObjectToNegativationFilter(value)) || []),
      ];

      return [
        {
          component: 'NegativationFilter',
          filterType: FILTER_NAMES.GROUP,
          props: {
            label: this.$t('components.filter.ModalFilters.label.sacListening.groups'),
            selectItems: namesItems,
            searchable: true,
            clearable: this.displayClearable(FILTER_NAMES.GROUP),
          },
        },
        {
          component: 'NegativationFilter',
          filterType: FILTER_NAMES.THEME,
          props: {
            label: this.$t('components.filter.ModalFilters.label.sacListening.themes'),
            selectItems: themeItems,
            searchable: true,
            clearable: this.displayClearable(FILTER_NAMES.THEME),
          },
        },
        {
          component: 'NegativationFilter',
          filterType: FILTER_NAMES.TAG,
          props: {
            label: this.$t('components.filter.ModalFilters.label.sacListening.tags'),
            selectItems: tagItems,
            searchable: true,
            clearable: this.displayClearable(FILTER_NAMES.TAG),
            hasTooltip: true,
          },
        },
        {
          component: 'ChannelFilter',
          filterType: FILTER_NAMES.CHANNELS_LISTENING,
          props: {
            clearable: this.displayClearable(FILTER_NAMES.CHANNELS_LISTENING),
          },
        },
        {
          component: 'DarkPostFilter',
          filterType: FILTER_NAMES.DARK_POST,
          props: {
            clearable: this.displayClearable(FILTER_NAMES.DARK_POST),
          },
        },
        {
          component: 'CheckedPosts',
          filterType: FILTER_NAMES.FAVORITE,
        },
      ];
    },
    getUIDProps() {
      return this.activeDashboardFilters?.uid || [];
    },
    filterTitleLabel() {
      return `${this.filterLabel} - ${this.card.props.title}`;
    },
    showClearChanges() {
      return !(isEqual(this.appliedFilter, this.tempFilter)) || this.changeOnPageFilter;
    },
    showClearFilter() {
      return Object.keys(this.appliedFilter).length;
    },
  },
  watch: {
    'tempFilter.pages': {
      handler(newPages, oldPages) {
        if (!isEqual(oldPages, newPages) && Boolean(newPages?.value?.length)) {
          this.channels = Array.from(new Set(this.pagesFormatted
            .filter(({ value }) => newPages?.value.includes(value))
            .map(({ channel }) => channel)));
          this.tempFilter.channels = { value: this.channels };
        }

        if (!isEqual(oldPages, newPages) && !Boolean(newPages?.value?.length)) {
          delete this.tempFilter.channels;
          delete this.tempFilter.pages;
        }
      },
      deep: true,
    },
  },
  created() {
    this.globalFilterByDataType = this.filterByDataType[this.card.dataType];
    this.globalFilterByDataTypeToShow = this.globalFilterByDataType;

    if (this.card.props.filter && ('listeningFilter' in this.card.props.filter || 'sacFilter' in this.card.props.filter)) {
      this.appliedFilter = { ...this.card.props.filter.listeningFilter, ...this.card.props.filter.sacFilter };
      this.tempFilter = { ...this.card.props.filter.listeningFilter, ...this.card.props.filter.sacFilter };

      const keysToUse = differenceWith(Object.keys(this.globalFilterByDataType), Object.keys(this.tempFilter), isEqual);

      this.globalFilterByDataTypeToShow = Object.fromEntries(Object.entries(this.globalFilterByDataType).filter(global => keysToUse.includes(global[0])));
    }
  },
  methods: {
    closeDialog() {
      this.$emit('input', false);
    },
    clearFilter() {
      this.tempFilter = {
        listeningFilter: {},
        sacFilter: {},
      };

      this.$emit('applyFilter', this.tempFilter);
      this.closeDialog();
    },
    applyFilter() {
      for (const filter in this.tempFilter) {
        if (!this.tempFilter[filter] || (!this.tempFilter[filter]?.value && this.tempFilter[filter]?.value?.length)) {
          delete this.tempFilter[filter];
        }
      }

      const listeningFilter = this.createListeningFilter();
      const sacFilter = this.createSacFilter();

      this.tempFilter = {
        listeningFilter,
        sacFilter,
      };

      this.$emit('applyFilter', this.tempFilter);

      this.closeDialog();
    },
    clearChangesFilter() {
      this.tempFilter = cloneDeep(this.appliedFilter);
    },
    generateObjectToFilter(value) {
      return {
        text: value,
        value,
      };
    },
    displayClearable(componentKey) {
      return Boolean(this.showGlobalFilterFeature && this.globalFilterByDataType?.hasOwnProperty(componentKey));
    },
    trackEvent(label) {
      trackEventWithUserProperties('ST Card Filter Delete', { Field: label, 'Data Type': DATA_TYPE.SAC_LISTENING });
    },
    createListeningFilter() {
      const listeningFilter = {};
      const listeningFields = [FILTER_NAMES.GROUP, FILTER_NAMES.THEME, FILTER_NAMES.TAG, FILTER_NAMES.CHANNELS_LISTENING, FILTER_NAMES.DARK_POST, FILTER_NAMES.FAVORITE, FILTER_NAMES.UIDS];

      for (const filter in this.tempFilter) {
        if (listeningFields.includes(filter)) {
          listeningFilter[filter] = this.tempFilter[filter];
        }
      }

      if (listeningFilter.channels_listening) {
        listeningFilter.channels = listeningFilter[FILTER_NAMES.CHANNELS_LISTENING];
      }

      return listeningFilter;
    },
    createSacFilter() {
      const sacFilter = {};
      const sacFields = [FILTER_NAMES.GROUP, FILTER_NAMES.THEME, FILTER_NAMES.TAG, FILTER_NAMES.FAVORITE, FILTER_NAMES.UIDS, FILTER_NAMES.PAGES, FILTER_NAMES.CHANNEL, FILTER_NAMES.SAC_TYPE];

      for (const k in this.tempFilter) {
        if (sacFields.includes(k)) {
          sacFilter[k] = this.tempFilter[k];
        }
      }
      return sacFilter;
    },
    changeFilterValue() {
      this.changeOnPageFilter = true;
    },
  },
};
</script>

<style scoped lang="scss">
  .align-items-center {
    align-items: center;
  }
</style>
